import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import GraphqlService from '../../service/graphqlService';
import { useProject } from '../../config/ProjectContext';
import { useSnackbar } from '../../providers/SnackbarContext';

export default function StagedDeploymentDetails({ deployment, onUpdate }) {
  const { project } = useProject();
  const openSnackBar = useSnackbar();
  
  const { canaryMetadata } = deployment;
  
  // Set default value to affectedSubset + 1 (one more than the denominator of Stage Status)
  const [absoluteValue, setAbsoluteValue] = useState(canaryMetadata?.affectedSubset ? canaryMetadata.affectedSubset + 1 : '');
  const [relativeValue, setRelativeValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Determine input type based on subsetType
  const isRelativeType = canaryMetadata?.subsetType === 'DevicePercentage';
  const isAbsoluteType = canaryMetadata?.subsetType === 'FiniteDeviceNumber';

  // Handle absolute value changes
  const handleAbsoluteChange = (value) => {
    setAbsoluteValue(value);
    
    // Real-time validation - cannot decrease below current affectedSubset
    if (value && parseInt(value, 10) <= canaryMetadata.affectedSubset) {
      setValidationError(`Value cannot be decreased. Must be greater than ${canaryMetadata.affectedSubset}`);
    } else {
      setValidationError('');
    }
  };

  // Handle relative value changes
  const handleRelativeChange = (value) => {
    setRelativeValue(value);
    
    // Real-time validation for relative values (if needed)
    if (value && parseInt(value, 10) <= canaryMetadata.affectedSubset) {
      setValidationError(`Value must be greater than ${canaryMetadata.affectedSubset}`);
    } else {
      setValidationError('');
    }
  };

  const handleResumeDeployment = useCallback(async () => {
    const currentValue = isAbsoluteType ? absoluteValue : relativeValue;
    
    if (!currentValue) {
      openSnackBar(`Please enter a ${isAbsoluteType ? 'absolute' : 'relative'} value for resuming deployment`, 'error');
      return;
    }

    const numericValue = parseInt(currentValue, 10);
    
    // Validation: the new value must be greater than affectedSubset (cannot decrease)
    if (numericValue <= canaryMetadata.affectedSubset) {
      openSnackBar(`Value cannot be decreased. The ${isAbsoluteType ? 'absolute' : 'relative'} value must be greater than the current affected subset (${canaryMetadata.affectedSubset})`, 'error');
      return;
    }

    // Clear validation error if we get here
    setValidationError('');

    setIsUpdating(true);
    try {
      // Calculate both absolute and relative values
      let absoluteValueToSend;
      let relativeValueToSend;

      if (isAbsoluteType) {
        // User entered absolute, calculate relative
        absoluteValueToSend = numericValue;
        relativeValueToSend = canaryMetadata.numberOfCandidates > 0 
          ? (numericValue / canaryMetadata.numberOfCandidates) * 100 
          : 0;
      } else {
        // User entered relative, calculate absolute
        relativeValueToSend = numericValue;
        absoluteValueToSend = canaryMetadata.numberOfCandidates > 0
          ? Math.round((numericValue / 100) * canaryMetadata.numberOfCandidates)
          : 0;
      }

      // Build request body with both values
      const requestBody = {
        CanaryStage: {
          Absolute: absoluteValueToSend,
          Relative: relativeValueToSend
        }
      };

      await GraphqlService.updateDeployment(
        project.code,
        deployment.deploymentID,
        requestBody
      );

      openSnackBar('Deployment updated successfully', 'success');
      onUpdate?.();
      
      // Reset form - set absolute value back to affectedSubset + 1
      setAbsoluteValue(canaryMetadata.affectedSubset + 1);
      setRelativeValue('');
    } catch (error) {
      openSnackBar(`Error updating deployment: ${error.message}`, 'error');
    } finally {
      setIsUpdating(false);
    }
  }, [absoluteValue, relativeValue, isAbsoluteType, project.code, deployment.deploymentID, openSnackBar, onUpdate, canaryMetadata.affectedSubset, canaryMetadata.numberOfCandidates]);

  const handleFullDeploy = useCallback(async () => {
    setIsUpdating(true);
    try {
      const requestBody = {
        CanaryStage: { Full: true }
      };

      await GraphqlService.updateDeployment(
        project.code,
        deployment.deploymentID,
        requestBody
      );

      openSnackBar('Deployment switched to full deployment', 'success');
      onUpdate?.();
    } catch (error) {
      openSnackBar(`Error switching to full deployment: ${error.message}`, 'error');
    } finally {
      setIsUpdating(false);
    }
  }, [project.code, deployment.deploymentID, openSnackBar, onUpdate]);

  if (!canaryMetadata) {
    return null;
  }

  // Calculate progress percentage based on the current stage (affectedSubset)
  const progressPercentage = canaryMetadata.affectedSubset > 0 
    ? Math.min((canaryMetadata.alreadySentTo / canaryMetadata.affectedSubset) * 100, 100)
    : 0;

  const getStatusColor = () => {
    // Green takes priority when stage is complete, regardless of pause status
    if (canaryMetadata.alreadySentTo >= canaryMetadata.affectedSubset) return '#4CAF50'; // Green when stage is complete
    if (deployment.deployPaused === true) return '#2196F3'; // Blue for paused
    if (canaryMetadata.alreadySentTo > 0) return '#FF9800'; // Orange when in progress
    return '#9E9E9E'; // Gray for not started
  };

  const getStatusText = () => {
    // Show completed status first, regardless of pause status
    if (canaryMetadata.alreadySentTo >= canaryMetadata.affectedSubset) return 'STAGE COMPLETED';
    if (deployment.deployPaused === true) return 'PAUSED';
    if (canaryMetadata.alreadySentTo > 0) return 'IN PROGRESS';
    return 'PENDING';
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg mt-4">
      <h3 className="text-lg font-bold mb-4">
        Stage Details
      </h3>

      {/* Stage Status */}
      <div className="flex items-center gap-4 mb-4">
        <span className="font-bold text-sm">Stage Status:</span>
        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className="h-full transition-all duration-300"
            style={{ 
              width: `${progressPercentage}%`,
              backgroundColor: getStatusColor()
            }}
          />
        </div>
        <span className="text-sm">
          {canaryMetadata.alreadySentTo} / {canaryMetadata.affectedSubset}
        </span>
        <span 
          className="px-2 py-1 rounded text-white text-xs font-bold"
          style={{ backgroundColor: getStatusColor() }}
        >
          {getStatusText()}
        </span>
      </div>

      {/*  Staged Deployment */}
      {canaryMetadata.alreadySentTo < canaryMetadata.affectedSubset && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 ">
                    <h4 className="font-bold text-sm">Resume Staged Deployment</h4>
                    <div className="relative group">
                    <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <div className="absolute left-0 bottom-6 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        You can continue the current deployment by increasing its deployment values. They must be higher than the current affected subset ({canaryMetadata.affectedSubset}).
                    </div>
                    </div>
                </div>

                {/* Input based on subsetType */}
                <div className="flex items-center gap-4 ">
                    {/* Show Absolute input for FiniteDeviceNumber */}
                    {isAbsoluteType && (
                    <>
                        <div className="flex items-center gap-2">
                        <label className="text-sm font-medium">Absolute:</label>
                        <input
                            type="number"
                            value={absoluteValue}
                            onChange={(e) => handleAbsoluteChange(e.target.value)}
                            placeholder="0"
                            min={canaryMetadata.affectedSubset + 1}
                            max={canaryMetadata.numberOfCandidates}
                            className={`w-20 px-3 py-2 text-center border rounded-md focus:outline-none focus:ring-2 ${
                              validationError && isAbsoluteType 
                                ? 'border-red-500 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                        />
                        <span className="text-sm text-gray-500">
                            / {canaryMetadata.numberOfCandidates}
                        </span>
                        </div>
                        <button
                        onClick={handleResumeDeployment}
                        disabled={isUpdating || !absoluteValue || validationError}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                        Update
                        </button>
                    </>
                    )}

                    {/* Show Relative input for DevicePercentage */}
                    {isRelativeType && (
                    <>
                        <div className="flex items-center gap-2">
                        <label className="text-sm font-medium">Relative:</label>
                        <input
                            type="number"
                            value={relativeValue}
                            onChange={(e) => handleRelativeChange(e.target.value)}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="1"
                            className={`w-20 px-3 py-2 text-center border rounded-md focus:outline-none focus:ring-2 ${
                              validationError && isRelativeType 
                                ? 'border-red-500 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                        />
                        <span className="text-sm text-gray-500">%</span>
                        </div>
                        <button
                        onClick={handleResumeDeployment}
                        disabled={isUpdating || !relativeValue || validationError}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                        Update
                        </button>
                    </>
                    )}

                    {/* Show message if subsetType is not recognized */}
                    {!isAbsoluteType && !isRelativeType && (
                    <div className="text-sm text-gray-500">
                        Unknown subset type: {canaryMetadata?.subsetType}
                    </div>
                    )}
                </div>
                
                {/* Error message */}
                {validationError && (
                  <div className="mt-2 text-red-600 text-sm font-medium">
                    ⚠️ {validationError}
                  </div>
                )}
            </div>     
        </div>
      )}

      {/* Switch to Full Deployment */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mt-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-sm">Switch to Full Deployment</h4>
            <div className="relative group">
              <svg className="w-4 h-4 text-gray-500 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <div className="absolute left-0 bottom-6 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                This action will make the staged deployment available for all the devices eligible for this deployment.
              </div>
            </div>
          </div>
          <button
            onClick={handleFullDeploy}
            disabled={isUpdating}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Fully Deploy
          </button>
        </div>
      </div>
    </div>
  );
}

StagedDeploymentDetails.propTypes = {
  deployment: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
};

StagedDeploymentDetails.defaultProps = {
  onUpdate: null,
};